import { useRef, useState } from 'react';

// Modelled after the native version (https://developer.mozilla.org/en-US/docs/Web/API/ValidityState)
export type InputValidityState<
  T extends Record<string, boolean> = Record<string, boolean>,
> = T & {
  valid: boolean;
};

export type ValidationHandler<T, S extends InputValidityState> = (event: {
  value: T;
  validity: S;
}) => string | null | undefined | void;

export type InputValidation = {
  valid: boolean | undefined;
  errorMessage: string;
  setFocus: (focus: boolean) => void;
  hasFocus: boolean;
  forceValidation: () => void;
};

export type ValidationOptions = {
  indeterminate: boolean;
  override?: boolean;
  validity: InputValidityState;
  validate: () => string | null | undefined | void;
};

/**
 * Provides consistent input validation behavior
 */
export function useInputValidation({
  indeterminate,
  override,
  validity,
  validate,
}: ValidationOptions): InputValidation {
  const [focus, setFocus] = useState(false);
  const [, forceUpdate] = useState<unknown>();

  const getState = () => {
    if (override !== undefined) {
      return { valid: override, errorMessage: '' };
    }
    if (indeterminate) {
      return { valid: undefined, errorMessage: '' };
    }
    const message = validate() ?? null;
    return {
      valid: message == null ? validity.valid : !message,
      errorMessage: message ?? '',
    };
  };

  // While the field is focussed, the validation state can only change if the
  // input is already invalid (it can't go from valid to invalid)
  let state = getState();
  const focusState = useRef(state);
  if (!focus || focusState.current.valid === false) {
    focusState.current = state;
  }
  if (focus) {
    state = focusState.current;
  }

  return {
    ...state,
    setFocus,
    hasFocus: focus,
    forceValidation: () => {
      focusState.current = getState();
      forceUpdate({});
    },
  };
}

/**
 * Create a valid InputValidityState object and set the global 'valid' property
 */
export function createValidityState<T extends Record<string, boolean>>(
  state: T,
): InputValidityState<T> {
  const valid = Object.values(state).every((x) => !x);
  return { ...state, valid };
}
